import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/TranslationContext";
import { claimReferral } from "../api/referrals";
import { clearInvite, getStoredInvite } from "../utils/invite";
import { takeAuthRedirect } from "../utils/authRedirect";

/**
 * A Google or Facebook sign-up can't carry an invite code through the provider, so a
 * waiting one is sent as soon as they're back. The server only attaches an account made
 * in the last day, so an existing user signing in here is simply turned away.
 */
async function claimWaitingInvite() {
  const code = getStoredInvite();
  if (!code) return;
  try {
    await claimReferral(code);
    clearInvite();
  } catch {
    /* kept for the next sign-in — it never blocks this one */
  }
}

const OAuthCallbackPage = ({ setToast }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();

  useEffect(() => {
    const token = searchParams.get("token");
    const error = searchParams.get("error");

    if (error) {
      setToast(t("oauth.failed"));
      navigate("/auth");
      return;
    }

    if (token) {
      loginWithToken(token)
        .then(async (result) => {
          if (result?.success) await claimWaitingInvite();
          setToast(t("oauth.success"));
          navigate(takeAuthRedirect() || "/");
        })
        .catch(() => {
          setToast(t("oauth.completeFailed"));
          navigate("/auth");
        });
    } else {
      setToast(t("oauth.noToken"));
      navigate("/auth");
    }
  }, [searchParams, navigate, loginWithToken, setToast, t]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-default mx-auto"></div>
        <p className="mt-4 text-slate-600 dark:text-slate-300">{t("oauth.completingSignIn")}</p>
      </div>
    </div>
  );
};

export default OAuthCallbackPage;
